import React, { useState, useCallback } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import { User } from '../../types';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { updateUserProfile } from '../../store/actions/user.actions';
import { updateLocation, clearLocation } from '../../api/userApi';
import { getAvatarColors } from '../../utils/avatarColors';

interface FamilyMapProps {
  members: User[];
  onLocationUpdate: (location: { lat: number; lng: number }) => void;
}

interface MapComponentProps {
  center: { lat: number; lng: number };
  zoom: number;
  members: User[];
  currentUser?: User | null;
}

const MapComponent: React.FC<MapComponentProps> = ({ center, zoom, members, currentUser }) => {
  const mapRef = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<google.maps.Map>();

  React.useEffect(() => {
    if (mapRef.current && !map) {
      const newMap = new google.maps.Map(mapRef.current, {
        center,
        zoom,
        styles: [
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });
      setMap(newMap);
    }
  }, [mapRef, map, center, zoom]);

  React.useEffect(() => {
    if (!map) return;

    const bounds = new google.maps.LatLngBounds();
    let hasValidLocations = false;

    // Create marker with photo overlay using custom OverlayView
    class PhotoMarker extends google.maps.OverlayView {
      private position: google.maps.LatLng;
      private photoUrl: string | undefined;
      private initials: string;
      private div?: HTMLDivElement;
      private memberName: string;
      private occupation: string;
      private phoneNumber: string | undefined;
      private avatarColor: string | undefined;

      constructor(
        position: google.maps.LatLng,
        photoUrl: string | undefined,
        initials: string,
        memberName: string,
        occupation: string,
        phoneNumber: string | undefined,
        avatarColor: string | undefined
      ) {
        super();
        this.position = position;
        this.photoUrl = photoUrl;
        this.initials = initials;
        this.memberName = memberName;
        this.occupation = occupation;
        this.phoneNumber = phoneNumber;
        this.avatarColor = avatarColor;
      }

      onAdd() {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.cursor = 'pointer';
        div.style.width = '50px';
        div.style.height = '50px';

        // Create circular container
        const circle = document.createElement('div');
        // Get avatar colors based on member name and custom color
        const avatarColors = getAvatarColors(this.memberName, this.avatarColor);

        circle.style.width = '100%';
        circle.style.height = '100%';
        circle.style.borderRadius = '50%';
        circle.style.border = '3px solid white';
        circle.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
        circle.style.overflow = 'hidden';
        circle.style.backgroundColor = avatarColors.backgroundColor;
        circle.style.display = 'flex';
        circle.style.alignItems = 'center';
        circle.style.justifyContent = 'center';

        if (this.photoUrl) {
          // Use photo as background
          circle.style.backgroundImage = `url(${this.photoUrl})`;
          circle.style.backgroundSize = 'cover';
          circle.style.backgroundPosition = 'center';
        } else {
          // Show initials
          circle.style.color = avatarColors.color;
          circle.style.fontSize = '22px';
          circle.style.fontWeight = 'bold';
          circle.style.fontFamily = 'Arial';
          circle.textContent = this.initials;
        }

        div.appendChild(circle);

        // Add click listener for info window
        div.addEventListener('click', () => {
          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div style="padding: 8px;">
                <h3 style="margin: 0 0 4px 0;">${this.memberName}</h3>
                <p style="margin: 0; color: #666;">${this.occupation}</p>
                ${this.phoneNumber ? `<p style="margin: 4px 0 0 0;"><strong>Phone:</strong> ${this.phoneNumber}</p>` : ''}
              </div>
            `,
            position: this.position
          });
          infoWindow.open(map);
        });

        this.div = div;
        const panes = this.getPanes();
        panes?.overlayMouseTarget.appendChild(div);
      }

      draw() {
        const overlayProjection = this.getProjection();
        const pos = overlayProjection.fromLatLngToDivPixel(this.position);

        if (this.div && pos) {
          this.div.style.left = (pos.x - 25) + 'px';
          this.div.style.top = (pos.y - 25) + 'px';
        }
      }

      onRemove() {
        if (this.div) {
          this.div.parentNode?.removeChild(this.div);
          delete this.div;
        }
      }
    }

    // Create markers for each member
    members.forEach((member) => {
      if (member.location && (member.isLocationVisible || member.is_location_visible)) {
        const photoUrl = member.photoURL || member.photo_url;
        const initials = (member.displayName || member.display_name || member.firstName || member.first_name || '?').charAt(0).toUpperCase();
        const memberName = member.displayName || member.display_name || `${member.firstName || member.first_name || ''} ${member.lastName || member.last_name || ''}`.trim();
        const memberPhone = member.phoneNumber || member.phone_number;

        console.log('FamilyMap: Creating marker for', memberName, 'with photoUrl:', photoUrl);

        const photoMarker = new PhotoMarker(
          new google.maps.LatLng(member.location.lat, member.location.lng),
          photoUrl,
          initials,
          memberName,
          member.occupation || 'Family Member',
          memberPhone,
          member.avatar_color
        );
        photoMarker.setMap(map);

        bounds.extend(member.location);
        hasValidLocations = true;
      }
    });

    if (hasValidLocations && members.length > 1) {
      map.fitBounds(bounds);
    }
  }, [map, members]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%' }} />;
};

const render = (status: Status) => {
  switch (status) {
    case Status.LOADING:
      return <CircularProgress />;
    case Status.FAILURE:
      return <Alert severity="error">Error loading Google Maps</Alert>;
    case Status.SUCCESS:
      return <></>;
    default:
      return <></>;
  }
};

export const FamilyMap: React.FC<FamilyMapProps> = ({ members, onLocationUpdate }) => {
  const dispatch = useAppDispatch();
  const { loginUser: user } = useAppSelector((state) => state.user);
  const [locationSharing, setLocationSharing] = useState(Boolean(user?.isLocationVisible ?? user?.is_location_visible ?? false));
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Sync location sharing state with user state from Redux
  React.useEffect(() => {
    const isLocationVisible = user?.isLocationVisible ?? user?.is_location_visible;
    if (isLocationVisible !== undefined) {
      setLocationSharing(Boolean(isLocationVisible));
    }
  }, [user?.isLocationVisible, user?.is_location_visible]);

  const handleLocationSharing = useCallback(async (enabled: boolean) => {
    if (enabled) {
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by this browser.');
        setLocationSharing(false);
        return;
      }

      // Don't change the toggle state yet - wait for permission
      try {
        // Check permission status first
        if ('permissions' in navigator) {
          const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
          console.log('Geolocation permission status:', permissionStatus.state);
        }

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            console.log('Location obtained:', position.coords.latitude, position.coords.longitude);
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            setUserLocation(location);
            onLocationUpdate(location);

            // Now set the toggle to on
            setLocationSharing(true);

            // Update location visibility in profile
            await dispatch(updateUserProfile({ isLocationVisible: true }));

            // Update location coordinates via dedicated API
            await updateLocation({
              latitude: location.lat,
              longitude: location.lng
            });
          },
          (error) => {
            console.error('Error getting location:', error);
            let errorMessage = 'Unable to get your location. ';
            if (error.code === error.PERMISSION_DENIED) {
              errorMessage += 'Location permission was denied. Please enable location access in your browser settings.';
            } else if (error.code === error.POSITION_UNAVAILABLE) {
              errorMessage += 'Location information is unavailable.';
            } else if (error.code === error.TIMEOUT) {
              errorMessage += 'Location request timed out.';
            }
            alert(errorMessage);
            setLocationSharing(false);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      } catch (error) {
        console.error('Permission check error:', error);
        setLocationSharing(false);
      }
    } else {
      // Clear user location when disabling sharing
      setLocationSharing(false);
      setUserLocation(null);
      await dispatch(updateUserProfile({ isLocationVisible: false }));

      // Clear location data from backend
      try {
        await clearLocation();
      } catch (error) {
        console.error('Failed to clear location data:', error);
      }
    }
  }, [onLocationUpdate, dispatch]);

  const locationEnabledMembers = members.filter(member =>
    member.isLocationVisible || member.is_location_visible
  );

  const visibleMembers = members.filter(member =>
    member.location && (member.isLocationVisible || member.is_location_visible)
  );

  // Debug logging to help troubleshoot
  // React.useEffect(() => {
  //   console.log('FamilyMap: Members received:', members.length);
  //   console.log('FamilyMap: Members with location data:', members.filter(m => m.location).length);
  //   console.log('FamilyMap: Members with location visible:', members.filter(m => m.isLocationVisible || m.is_location_visible).length);
  //   console.log('FamilyMap: Visible members (location + visible):', visibleMembers.length);
  //   if (members.length > 0) {
  //     console.log('FamilyMap: Sample member data:', {
  //       location: members[0].location,
  //       isLocationVisible: members[0].isLocationVisible,
  //       is_location_visible: members[0].is_location_visible
  //     });
  //   }
  // }, [members, visibleMembers]);

  const defaultCenter = userLocation ||
    (visibleMembers.length > 0 ? visibleMembers[0].location! : { lat: 39.8283, lng: -98.5795 });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Family Locations ({visibleMembers.length} with GPS, {locationEnabledMembers.length} sharing enabled)
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={locationSharing}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLocationSharing(e.target.checked)}
              />
            }
            label="Share my location"
          />
        </Box>

        {!locationSharing && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Enable location sharing to see your position and let family find you easily.
          </Alert>
        )}

        {locationEnabledMembers.length > 0 && visibleMembers.length === 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {locationEnabledMembers.length} family member{locationEnabledMembers.length > 1 ? 's have' : ' has'} enabled location sharing but haven't shared GPS coordinates yet. Toggle "Share my location" to add your position to the map.
          </Alert>
        )}

        {locationEnabledMembers.length > visibleMembers.length && visibleMembers.length > 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {locationEnabledMembers.length - visibleMembers.length} more family member{(locationEnabledMembers.length - visibleMembers.length) > 1 ? 's' : ''} {(locationEnabledMembers.length - visibleMembers.length) > 1 ? 'have' : 'has'} enabled sharing but need to share their current location.
          </Alert>
        )}
      </Paper>

      <Paper sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Wrapper
          apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
          render={render}
        >
          <MapComponent
            center={defaultCenter}
            zoom={10}
            members={visibleMembers}
            currentUser={user}
          />
        </Wrapper>
      </Paper>
    </Box>
  );
};